import {useCallback, useEffect, useState} from 'react';

import {API_CONFIG} from '../../contentGenerator/config';
import {apiFetch} from '../apiFetch';

const STATUS_URL = `${API_CONFIG.PROXY_URL}/gmc/auth/status`;
const SETTINGS_URL = `${API_CONFIG.PROXY_URL}/gmc/settings`;
const DISCONNECT_URL = `${API_CONFIG.PROXY_URL}/gmc/auth/disconnect`;
const AUTH_START_URL = `${API_CONFIG.PROXY_URL}/gmc/auth/start`;

const DEFAULT_SETTINGS = {
    merchantId: '',
    supplementalFeedId: '',
    targetCountry: 'US',
    contentLanguage: 'en',
    offerIdTemplate: 'shopify_{COUNTRY}_{PRODUCT_ID}_{VARIANT_ID}',
    feedSources: {},
};

const normalizeFeedSources = (value) => {
    if (!value || typeof value !== 'object') return {};
    const out = {};
    for (const [key, entry] of Object.entries(value)) {
        if (typeof key !== 'string' || !key.trim()) continue;
        if (!entry || typeof entry !== 'object') continue;
        const dataSourceId = typeof entry.dataSourceId === 'string' ? entry.dataSourceId.trim() : '';
        if (!dataSourceId) continue;
        out[key.trim()] = {
            dataSourceId,
            contentLanguage:
                typeof entry.contentLanguage === 'string' && entry.contentLanguage.trim()
                    ? entry.contentLanguage.trim()
                    : '',
            displayName: typeof entry.displayName === 'string' ? entry.displayName : '',
        };
    }
    return out;
};

const normalizeSettings = (value) => {
    if (!value || typeof value !== 'object') return {...DEFAULT_SETTINGS, feedSources: {}};
    return {
        merchantId: typeof value.merchantId === 'string' ? value.merchantId : '',
        supplementalFeedId:
            typeof value.supplementalFeedId === 'string' ? value.supplementalFeedId : '',
        targetCountry:
            typeof value.targetCountry === 'string' && value.targetCountry
                ? value.targetCountry
                : DEFAULT_SETTINGS.targetCountry,
        contentLanguage:
            typeof value.contentLanguage === 'string' && value.contentLanguage
                ? value.contentLanguage
                : DEFAULT_SETTINGS.contentLanguage,
        offerIdTemplate:
            typeof value.offerIdTemplate === 'string' && value.offerIdTemplate
                ? value.offerIdTemplate
                : DEFAULT_SETTINGS.offerIdTemplate,
        feedSources: normalizeFeedSources(value.feedSources),
    };
};

async function parseApiResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) {
        throw new Error(
            payload?.error
                || payload?.message
                || (typeof payload === 'string' && payload)
                || fallbackMessage,
        );
    }

    return payload;
}

export async function fetchGmcStatus() {
    const response = await apiFetch(STATUS_URL);
    const data = await parseApiResponse(response, 'Could not load Google Merchant status.');
    return {
        connected: Boolean(data?.connected),
        email: data?.email || '',
        connectedAt: data?.connectedAt || null,
        hasOAuthCredentials: Boolean(data?.hasOAuthCredentials),
        settings: normalizeSettings(data?.settings),
    };
}

export async function saveGmcSettings(settings) {
    const response = await apiFetch(SETTINGS_URL, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(normalizeSettings(settings)),
    });
    const saved = await parseApiResponse(response, 'Could not save Google Merchant settings.');
    return normalizeSettings(saved);
}

export async function disconnectGmc() {
    const response = await apiFetch(DISCONNECT_URL, {method: 'POST'});
    await parseApiResponse(response, 'Could not disconnect Google Merchant.');
    return true;
}

export async function pushProductToGmc(productId, payload = {}) {
    const response = await apiFetch(
        `${API_CONFIG.PROXY_URL}/gmc/products/${encodeURIComponent(productId)}/push`,
        {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(payload),
        },
    );
    return parseApiResponse(response, 'Could not push product to Google Merchant.');
}

export async function fetchGmcAccounts() {
    const response = await apiFetch(`${API_CONFIG.PROXY_URL}/gmc/accounts`);
    const data = await parseApiResponse(response, 'Could not load Google Merchant accounts.');
    return Array.isArray(data?.accounts) ? data.accounts : [];
}

export async function fetchGmcDataSources(merchantId) {
    if (!merchantId) return [];
    const response = await apiFetch(
        `${API_CONFIG.PROXY_URL}/gmc/data-sources?merchantId=${encodeURIComponent(merchantId)}`,
    );
    const data = await parseApiResponse(response, 'Could not load Google Merchant data sources.');
    return Array.isArray(data?.dataSources) ? data.dataSources : [];
}

export async function registerGmcDeveloper(merchantId) {
    if (!merchantId) {
        throw new Error('Merchant ID is required.');
    }
    const response = await apiFetch(`${API_CONFIG.PROXY_URL}/gmc/register-developer`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({merchantId}),
    });
    return parseApiResponse(
        response,
        'Could not register the GCP project with this Merchant Center account.',
    );
}

export async function startGmcConnect() {
    if (typeof window === 'undefined') return null;
    const popup = window.open(
        'about:blank',
        'gmc-oauth',
        'width=520,height=640,menubar=no,toolbar=no',
    );
    if (!popup) return null;

    try {
        const response = await apiFetch(AUTH_START_URL, {method: 'POST'});
        const data = await parseApiResponse(response, 'Could not start Google Merchant auth.');
        if (!data?.authUrl) {
            throw new Error('Google Merchant auth URL was not returned.');
        }
        popup.location.href = data.authUrl;
    } catch (err) {
        popup.close();
        throw err;
    }

    return popup;
}

export function useGmc() {
    const [status, setStatus] = useState({
        connected: false,
        email: '',
        connectedAt: null,
        hasOAuthCredentials: false,
        settings: DEFAULT_SETTINGS,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const next = await fetchGmcStatus();
            setStatus(next);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const connect = useCallback(async () => {
        let popup = null;
        try {
            popup = await startGmcConnect();
        } catch (err) {
            setError(err.message);
            return;
        }
        if (!popup) {
            setError('Could not open the Google Merchant sign-in window.');
            return;
        }

        const interval = setInterval(() => {
            if (popup.closed) {
                clearInterval(interval);
                refresh();
            }
        }, 800);
    }, [refresh]);

    const disconnect = useCallback(async () => {
        await disconnectGmc();
        await refresh();
    }, [refresh]);

    const updateSettings = useCallback(
        async (nextSettings) => {
            const saved = await saveGmcSettings(nextSettings);
            setStatus((current) => ({...current, settings: saved}));
            return saved;
        },
        [],
    );

    const pushProduct = useCallback(async (productId, payload) => {
        return pushProductToGmc(productId, payload);
    }, []);

    return {
        status,
        loading,
        error,
        refresh,
        connect,
        disconnect,
        updateSettings,
        pushProduct,
    };
}
